'use client';

import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { TerminalWordmark } from './TerminalWordmark';
import { PriceChart } from './PriceChart';
import { SpxVixChart } from './SpxVixChart';
import { StatGrid } from './StatGrid';
import { ConstituentTable } from './ConstituentTable';
import { EconomyTab } from './EconomyTab';
import { formatPrice, formatPercent, getChangeColor } from '@/lib/terminal-format';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';

interface IndexEntry {
  key: string;
  label: string;
  chartSymbol: string;
  chartLabel: string;
}

interface IndexGroup {
  label: string;
  indices: IndexEntry[];
}

// Standalone indices shown as top-level tabs
const STANDALONE_INDICES: IndexEntry[] = [
  { key: 'nasdaq100', label: 'NASDAQ 100', chartSymbol: 'QQQ', chartLabel: 'NASDAQ 100 (QQQ)' },
  { key: 'russell2000', label: 'Russell 2000', chartSymbol: 'IWM', chartLabel: 'Russell 2000 (IWM)' },
];

// Grouped indices shown as dropdowns
const INDEX_GROUPS: IndexGroup[] = [
  {
    label: 'S&P',
    indices: [
      { key: 'sp500', label: 'S&P 500', chartSymbol: 'SPY', chartLabel: 'S&P 500 (SPY)' },
      { key: 'sp100', label: 'S&P 100', chartSymbol: 'OEF', chartLabel: 'S&P 100 (OEF)' },
      { key: 'sp400', label: 'S&P MidCap 400', chartSymbol: 'MDY', chartLabel: 'S&P MidCap 400 (MDY)' },
      { key: 'sp600', label: 'S&P SmallCap 600', chartSymbol: 'SLY', chartLabel: 'S&P SmallCap 600 (SLY)' },
      { key: 'sp-oil-gas', label: 'S&P Oil & Gas', chartSymbol: 'XOP', chartLabel: 'S&P Oil & Gas (XOP)' },
      { key: 'sp-real-estate', label: 'S&P Real Estate', chartSymbol: 'XLRE', chartLabel: 'S&P Real Estate (XLRE)' },
    ],
  },
  {
    label: 'Dow Jones',
    indices: [
      { key: 'dji', label: 'DJ Industrial', chartSymbol: 'DIA', chartLabel: 'Dow Jones Industrial (DIA)' },
      { key: 'djc', label: 'DJ Composite', chartSymbol: 'DIA', chartLabel: 'Dow Jones Composite (DIA)' },
      { key: 'djt', label: 'DJ Transportation', chartSymbol: 'IYT', chartLabel: 'Dow Jones Transportation (IYT)' },
      { key: 'dju', label: 'DJ Utilities', chartSymbol: 'IDU', chartLabel: 'Dow Jones Utilities (IDU)' },
    ],
  },
];

const INDEX_DESCRIPTIONS: Record<string, string> = {
  sp500: 'Tracks 500 of the largest U.S. companies by market cap, covering ~80% of available U.S. equity market capitalization.',
  sp100: 'A subset of the S&P 500 tracking the 100 largest U.S. companies. Constituents must have listed options.',
  sp400: 'Tracks 400 mid-cap U.S. companies with market caps typically between $5.2B and $14.5B.',
  sp600: 'Tracks 600 small-cap U.S. companies with market caps typically between $1B and $5.2B.',
  'sp-oil-gas': 'Tracks U.S. companies in the oil and gas exploration, production, and services industries.',
  'sp-real-estate': 'Tracks U.S. REITs and real estate companies including residential, commercial, and specialized properties.',
  nasdaq100: 'Tracks the 100 largest non-financial companies listed on the NASDAQ exchange, heavily weighted toward technology.',
  russell2000: 'Tracks 2,000 small-cap U.S. companies, widely used as a benchmark for small-cap performance.',
  dji: 'Price-weighted index of 30 prominent U.S. blue-chip companies, one of the oldest and most-watched market indicators.',
  djc: 'Combines all 65 stocks from the DJ Industrial, Transportation, and Utilities averages into a single broad index.',
  djt: 'Tracks 20 U.S. transportation companies including airlines, railroads, and trucking firms.',
  dju: 'Tracks 15 U.S. utility companies including electric, gas, and water providers.',
};

const COMMODITIES = [
  { symbol: 'CL=F', label: 'Crude Oil (WTI)' },
  { symbol: 'GC=F', label: 'Gold' },
  { symbol: 'SI=F', label: 'Silver' },
];

type TabId = 'commodities' | string; // string = index key

// Order of top-level tabs as they're rendered (used by keyboard shortcuts).
// 1: S&P group (defaults to S&P 500), 2: Dow Jones group (defaults to Industrial),
// 3: NASDAQ 100, 4: Russell 2000, 5: Commodities, 6: VIX, 7: Macro, 8: Economy.
export const MARKET_DATA_TAB_ORDER: TabId[] = [
  'sp500',
  'dji',
  'nasdaq100',
  'russell2000',
  'commodities',
  'vix',
  'macro',
  'economy',
];

interface MarketDataViewProps {
  onBack: () => void;
  onResearch: (symbol: string) => void;
  activeTab?: TabId;
  onTabChange?: (tab: TabId) => void;
}

export function MarketDataView({ onBack, onResearch, activeTab: controlledTab, onTabChange }: MarketDataViewProps) {
  const [internalTab, setInternalTab] = useState<TabId>('sp500');
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = (t: TabId) => {
    if (onTabChange) onTabChange(t);
    else setInternalTab(t);
  };
  const [groupOpen, setGroupOpen] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!groupOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setGroupOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [groupOpen]);

  // Find the active index entry (from standalone or groups)
  const activeIndex =
    STANDALONE_INDICES.find((i) => i.key === activeTab) ||
    INDEX_GROUPS.flatMap((g) => g.indices).find((i) => i.key === activeTab);

  const tabClass = (active: boolean) =>
    `px-3 md:px-4 py-2 text-xs font-mono uppercase tracking-wider whitespace-nowrap transition-colors border-b-2 ${
      active
        ? 'text-[var(--color-accent)] border-[var(--color-accent)]'
        : 'text-[#6B7280] border-transparent hover:text-[#E5E7EB]'
    }`;

  return (
    <div className="h-full flex flex-col bg-[#0A0E17]">
      {/* Header */}
      <div className="h-12 flex items-center justify-between px-4 bg-[#111827] border-b border-[#1F2937] flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-[#6B7280] hover:text-[var(--color-accent)] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
          </button>
          <TerminalWordmark />
          <span className="text-[#6B7280] text-xs font-mono">/</span>
          <span className="font-mono text-xs font-semibold tracking-wider text-[var(--color-accent)]">
            MARKET DATA
          </span>
        </div>
      </div>

      {/* Tab bar — wraps to multiple rows on narrow screens so dropdowns stay usable */}
      <div ref={dropdownRef} className="flex flex-wrap items-center border-b border-[#1F2937] bg-[#111827] flex-shrink-0">
        {/* Grouped dropdowns */}
        {INDEX_GROUPS.map((group) => {
          const isActiveGroup = group.indices.some((i) => i.key === activeTab);
          return (
            <div key={group.label} className="relative">
              <button
                onClick={() => setGroupOpen(groupOpen === group.label ? null : group.label)}
                className={`flex items-center gap-1 ${tabClass(isActiveGroup)}`}
              >
                {isActiveGroup ? activeIndex?.label : group.label}
                <ChevronDown className="w-3 h-3" />
              </button>
              {groupOpen === group.label && (
                <div className="absolute top-full left-0 mt-0 bg-[#111827] border border-[#1F2937] shadow-lg z-50 min-w-[180px]">
                  {group.indices.map((idx) => (
                    <button
                      key={idx.key}
                      onClick={() => {
                        setActiveTab(idx.key);
                        setGroupOpen(null);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs font-mono transition-colors ${
                        activeTab === idx.key
                          ? 'text-[var(--color-accent)] bg-[#1A2035]'
                          : 'text-[#E5E7EB] hover:bg-[#1A2035]'
                      }`}
                    >
                      {idx.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Standalone index tabs */}
        {STANDALONE_INDICES.map((idx) => (
          <button
            key={idx.key}
            onClick={() => { setActiveTab(idx.key); setGroupOpen(null); }}
            className={tabClass(activeTab === idx.key)}
          >
            {idx.label}
          </button>
        ))}

        {/* Commodities tab */}
        <button
          onClick={() => { setActiveTab('commodities'); setGroupOpen(null); }}
          className={tabClass(activeTab === 'commodities')}
        >
          Commodities
        </button>

        {/* VIX tab */}
        <button
          onClick={() => { setActiveTab('vix'); setGroupOpen(null); }}
          className={tabClass(activeTab === 'vix')}
        >
          VIX
        </button>

        {/* Macro tab */}
        <button
          onClick={() => { setActiveTab('macro'); setGroupOpen(null); }}
          className={tabClass(activeTab === 'macro')}
        >
          Macro
        </button>

        {/* Economy tab */}
        <button
          onClick={() => { setActiveTab('economy'); setGroupOpen(null); }}
          className={tabClass(activeTab === 'economy')}
        >
          Economy
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'commodities' ? (
          <CommoditiesTab />
        ) : activeTab === 'vix' ? (
          <VixTab />
        ) : activeTab === 'macro' ? (
          <MacroTab />
        ) : activeTab === 'economy' ? (
          <EconomyTab />
        ) : activeIndex ? (
          <IndexTab
            indexKey={activeIndex.key}
            chartSymbol={activeIndex.chartSymbol}
            chartLabel={activeIndex.chartLabel}
            onResearch={onResearch}
          />
        ) : null}
      </div>
    </div>
  );
}

function IndexTab({
  indexKey,
  chartSymbol,
  chartLabel,
  onResearch,
}: {
  indexKey: string;
  chartSymbol: string;
  chartLabel: string;
  onResearch: (symbol: string) => void;
}) {
  const { data: constituents, isLoading, error } = useQuery<
    { symbol: string; name: string; sector: string; marketCap?: number }[]
  >({
    queryKey: ['/api/terminal/index-constituents', indexKey],
    queryFn: async () => {
      const res = await fetch(`/api/terminal/index-constituents/${indexKey}`);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`${res.status}: ${body}`);
      }
      return res.json();
    },
    staleTime: 24 * 60 * 60 * 1000, // 24h client-side staleness
    retry: 2,
  });

  return (
    <div className="space-y-4">
      <div className="bg-[#111827] border border-[#1F2937] p-4">
        <div className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-1">
          {chartLabel}
        </div>
        {INDEX_DESCRIPTIONS[indexKey] && (
          <div className="text-[11px] text-[#6B7280] mb-3">
            {INDEX_DESCRIPTIONS[indexKey]}
          </div>
        )}
        <PriceChart symbol={chartSymbol} height={280} defaultRange="1M" />
      </div>
      <div>
        <div className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-2">
          Constituents {constituents ? `(${constituents.length})` : ''}
        </div>
        {isLoading ? (
          <div className="bg-[#111827] border border-[#1F2937] p-4 space-y-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="h-5 w-full bg-[#1F2937] animate-pulse rounded" />
            ))}
          </div>
        ) : constituents ? (
          <ConstituentTable data={constituents} onSelectSymbol={onResearch} />
        ) : (
          <div className="bg-[#111827] border border-[#1F2937] px-3 py-8 text-xs text-[#6B7280] text-center">
            Failed to load constituents{error ? `: ${error.message}` : ''}
          </div>
        )}
      </div>
    </div>
  );
}

function VixTab() {
  const { data: macro, isLoading } = useQuery<{
    vix?: { price: number; change: number; changePercent: number };
    indices?: Record<string, { price: number; change: number; changePercent: number }>;
  }>({
    queryKey: ['/api/terminal/macro'],
    queryFn: async () => {
      const res = await fetch('/api/terminal/macro');
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    refetchInterval: 60000,
  });

  const vix = macro?.vix;
  const spy = macro?.indices?.SPY;

  return (
    <div className="space-y-4">
      <div className="bg-[#111827] border border-[#1F2937] p-4">
        <div className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-1">
          CBOE VIX vs S&P 500
        </div>
        <div className="text-[11px] text-[#6B7280] mb-3">
          The CBOE Volatility Index (VIX) measures 30-day expected volatility of the S&P 500 derived from options prices. VIX and the S&P 500 typically move inversely: volatility spikes during equity drawdowns.
        </div>

        <div className="grid grid-cols-2 gap-px bg-[#1F2937] mb-4">
          <div className="bg-[#0D1117] p-3">
            <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1">VIX</div>
            {isLoading ? (
              <div className="h-6 w-20 bg-[#1F2937] animate-pulse rounded" />
            ) : (
              <>
                <div className="font-mono text-xl font-bold tabular-nums text-[#E5E7EB]">
                  {vix?.price ? vix.price.toFixed(2) : '—'}
                </div>
                <div className={`font-mono text-xs tabular-nums mt-1 ${getChangeColor(vix?.changePercent || 0)}`}>
                  {formatPercent(vix?.changePercent || 0)}
                </div>
              </>
            )}
          </div>
          <div className="bg-[#0D1117] p-3">
            <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1">S&P 500 (SPY)</div>
            {isLoading ? (
              <div className="h-6 w-20 bg-[#1F2937] animate-pulse rounded" />
            ) : (
              <>
                <div className="font-mono text-xl font-bold tabular-nums text-[#E5E7EB]">
                  {spy?.price ? formatPrice(spy.price) : '—'}
                </div>
                <div className={`font-mono text-xs tabular-nums mt-1 ${getChangeColor(spy?.changePercent || 0)}`}>
                  {formatPercent(spy?.changePercent || 0)}
                </div>
              </>
            )}
          </div>
        </div>

        <SpxVixChart height={320} defaultRange="3M" />
      </div>
    </div>
  );
}

function CommoditiesTab() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {COMMODITIES.map((c) => (
        <div key={c.symbol} className="bg-[#111827] border border-[#1F2937] p-4">
          <div className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-3">
            {c.label}
          </div>
          <PriceChart symbol={c.symbol} height={220} defaultRange="1M" />
        </div>
      ))}
    </div>
  );
}

function MacroTab() {
  const { data: macro, isLoading } = useQuery<Record<string, unknown>>({
    queryKey: ['/api/terminal/macro'],
    queryFn: async () => {
      const res = await fetch('/api/terminal/macro');
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    refetchInterval: 300000,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-48 w-full bg-[#1F2937] animate-pulse rounded" />
        <div className="h-24 w-full bg-[#1F2937] animate-pulse rounded" />
      </div>
    );
  }

  const m = macro || {};
  const yields = (m.yields as Record<string, number>) || {};
  const yieldData = [
    { term: '2Y', yield: yields['2Y'] || 0 },
    { term: '5Y', yield: yields['5Y'] || 0 },
    { term: '10Y', yield: yields['10Y'] || 0 },
    { term: '30Y', yield: yields['30Y'] || 0 },
  ];

  const indices = (m.indices as Record<string, { price: number; changePercent: number }>) || {};

  return (
    <div className="space-y-4">
      {/* Yield Curve */}
      <div className="bg-[#111827] border border-[#1F2937] p-4">
        <div className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-3">Treasury Yield Curve</div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={yieldData} margin={{ top: 4, right: 16, bottom: 0, left: 4 }}>
            <XAxis
              dataKey="term"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 11, fontFamily: 'JetBrains Mono' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              tickFormatter={(v) => v.toFixed(2) + '%'}
              domain={['dataMin - 0.2', 'dataMax + 0.2']}
              width={55}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#1A2035', border: '1px solid #1F2937', borderRadius: '2px', fontSize: '12px', fontFamily: 'JetBrains Mono', color: '#E5E7EB' }}
              formatter={(value: number | undefined) => [(value?.toFixed(2) ?? '0') + '%', 'Yield']}
            />
            <Line
              type="monotone"
              dataKey="yield"
              stroke="#3B82F6"
              strokeWidth={2}
              dot={{ fill: '#3B82F6', r: 4 }}
              activeDot={{ r: 6, fill: 'var(--color-accent)' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Economic Indicators */}
      <div className="bg-[#111827] border border-[#1F2937]">
        <div className="px-3 py-2 border-b border-[#1F2937]">
          <span className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider">Economic Indicators</span>
        </div>
        <StatGrid
          items={[
            { label: 'Fed Funds Rate', value: m.fedFundsRate ? (m.fedFundsRate as number).toFixed(2) + '%' : 'N/A' },
            { label: 'CPI (YoY)', value: m.cpi ? (m.cpi as number).toFixed(1) + '%' : 'N/A' },
            { label: 'Unemployment', value: m.unemploymentRate ? (m.unemploymentRate as number).toFixed(1) + '%' : 'N/A' },
            { label: 'GDP Growth', value: m.gdpGrowth ? (m.gdpGrowth as number).toFixed(1) + '%' : 'N/A' },
          ]}
          columns={4}
        />
      </div>

      {/* Major Index Performance */}
      <div className="bg-[#111827] border border-[#1F2937]">
        <div className="px-3 py-2 border-b border-[#1F2937]">
          <span className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider">Major Indices</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1F2937]">
          {Object.entries(indices).map(([key, val]) => (
            <div key={key} className="bg-[#111827] p-3">
              <div className="text-[10px] text-[#6B7280] uppercase mb-1">{key}</div>
              <div className="font-mono text-sm font-medium tabular-nums text-[#E5E7EB]">{formatPrice(val?.price || 0)}</div>
              <div className={`font-mono text-[10px] tabular-nums ${getChangeColor(val?.changePercent || 0)}`}>
                {formatPercent(val?.changePercent || 0)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* VIX & DXY */}
      <div className="grid grid-cols-2 gap-px bg-[#1F2937]">
        <div className="bg-[#111827] p-4">
          <div className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-2">VIX (Volatility Index)</div>
          <div className="font-mono text-2xl font-bold tabular-nums text-[#E5E7EB]">
            {(m.vix as { price: number })?.price ? ((m.vix as { price: number }).price).toFixed(2) : '—'}
          </div>
          <div className={`font-mono text-xs tabular-nums mt-1 ${getChangeColor((m.vix as { changePercent: number })?.changePercent || 0)}`}>
            {formatPercent((m.vix as { changePercent: number })?.changePercent || 0)}
          </div>
        </div>
        <div className="bg-[#111827] p-4">
          <div className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-2">DXY (USD Index)</div>
          <div className="font-mono text-2xl font-bold tabular-nums text-[#E5E7EB]">
            {(m.dxy as { price: number })?.price ? ((m.dxy as { price: number }).price).toFixed(2) : '—'}
          </div>
          <div className={`font-mono text-xs tabular-nums mt-1 ${getChangeColor((m.dxy as { changePercent: number })?.changePercent || 0)}`}>
            {formatPercent((m.dxy as { changePercent: number })?.changePercent || 0)}
          </div>
        </div>
      </div>
    </div>
  );
}
